// src/components/auth/BiometricLogin.jsx
// Component for biometric login on the login page

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { startAuthentication } from '@simplewebauthn/browser';
import { Button } from '@/components/ui/button';
import { Fingerprint, Loader2, AlertTriangle } from 'lucide-react';
import { logger } from '@/lib/logger';

export default function BiometricLogin({ email, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);

  useEffect(() => {
    checkBiometricAvailability();
  }, [email]);

  const checkBiometricAvailability = async () => {
    try {
      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        setAvailable(false);
        return;
      }

      // Check if platform authenticator is available
      const platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setAvailable(platformAvailable);

      // If email provided, check if user has registered credentials
      if (email && platformAvailable) {
        await checkUserHasCredential(email);
      }
    } catch (e) {
      logger.debug('Biometric availability check failed:', e);
      setAvailable(false);
    }
  };

  const checkUserHasCredential = async (userEmail) => {
    try {
      // Try to get auth options for this email - if it fails, no credentials exist
      const response = await supabase.functions.invoke('webauthn-auth-options', {
        body: { email: userEmail },
      });

      if (response.error || response.data?.error) {
        setHasCredential(false);
      } else {
        // User has at least one credential
        setHasCredential(true);
      }
    } catch (e) {
      setHasCredential(false);
    }
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Get authentication options
      const optionsResponse = await supabase.functions.invoke('webauthn-auth-options', {
        body: { email: email || undefined },
      });

      if (optionsResponse.error) {
        throw new Error(optionsResponse.error.message || 'Failed to get authentication options');
      }

      const options = optionsResponse.data;
      const userId = options._userId;

      logger.debug('Got authentication options');

      // Step 2: Start the WebAuthn authentication ceremony
      let credential;
      try {
        credential = await startAuthentication({ optionsJSON: options });
      } catch (authError) {
        if (authError.name === 'NotAllowedError') {
          throw new Error('Logowanie biometryczne zostało anulowane');
        } else if (authError.name === 'SecurityError') {
          throw new Error('Błąd bezpieczeństwa - sprawdź ustawienia przeglądarki');
        } else {
          throw authError;
        }
      }

      logger.debug('Authentication credential obtained');

      // Step 3: Verify with our Edge Function
      const verifyResponse = await supabase.functions.invoke('webauthn-auth-verify', {
        body: {
          credential,
          userId,
        },
      });

      if (verifyResponse.error) {
        throw new Error(verifyResponse.error.message || 'Verification failed');
      }

      if (!verifyResponse.data?.verified) {
        throw new Error(verifyResponse.data?.error || 'Verification failed');
      }

      const { token_hash, type, email: userEmail } = verifyResponse.data;

      logger.debug('Biometric authentication verified, completing sign-in');

      // Step 4: Complete the sign-in by verifying the OTP
      const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash,
        type,
      });

      if (verifyError) {
        throw verifyError;
      }

      logger.info('Biometric login successful for:', userEmail);
      onSuccess?.(sessionData);
    } catch (err) {
      logger.error('Biometric login error:', err);
      const errorMessage = err.message || 'Logowanie biometryczne nie powiodło się';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if biometric is not available
  if (!available) {
    return null;
  }

  // If email is provided but user has no credential, don't show the button
  if (email && !hasCredential) {
    return null;
  }

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        onClick={handleBiometricLogin}
        disabled={loading}
        className="w-full border-green-200 hover:bg-green-50 hover:border-green-300"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Weryfikacja...
          </>
        ) : (
          <>
            <Fingerprint className="h-4 w-4 mr-2 text-green-600" />
            Zaloguj biometrycznie
          </>
        )}
      </Button>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// Standalone button component for showing biometric option without email context
export function BiometricLoginButton({ onSuccess, onError, className = '' }) {
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    checkAvailability();
  }, []);

  const checkAvailability = async () => {
    try {
      if (!window.PublicKeyCredential) {
        setAvailable(false);
        return;
      }
      const platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setAvailable(platformAvailable);
    } catch (e) {
      setAvailable(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      // Get options without email - for discoverable credentials / passkeys
      const optionsResponse = await supabase.functions.invoke('webauthn-auth-options', {
        body: {},
      });

      if (optionsResponse.error) {
        throw new Error('Failed to get authentication options');
      }

      const options = optionsResponse.data;
      const credential = await startAuthentication({ optionsJSON: options });

      const verifyResponse = await supabase.functions.invoke('webauthn-auth-verify', {
        body: { credential },
      });

      if (verifyResponse.error) {
        throw new Error('Verification failed');
      }

      if (!verifyResponse.data?.verified) {
        throw new Error(verifyResponse.data?.error || 'Verification failed');
      }

      const { token_hash, type } = verifyResponse.data;
      const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash,
        type,
      });

      if (verifyError) throw verifyError;

      onSuccess?.(sessionData);
    } catch (err) {
      logger.error('Biometric login error:', err);
      onError?.(err.message || 'Logowanie nie powiodło się');
    } finally {
      setLoading(false);
    }
  };

  if (!available) return null;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleLogin}
      disabled={loading}
      className={`w-full ${className}`}
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <Fingerprint className="h-5 w-5" />
      )}
    </Button>
  );
}

// Check if user has any biometric credentials stored (for showing/hiding button)
export async function checkUserHasBiometric(email) {
  try {
    const response = await supabase.functions.invoke('webauthn-auth-options', {
      body: { email },
    });
    return !response.error && !response.data?.error;
  } catch (e) {
    return false;
  }
}
